import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'

import { Screen, ScreenHeader } from '../components/chrome/shell'
import { Icon } from '../components/icon'
import { Button } from '../components/ui/button'
import { Blank, Panel, Row, Skeleton } from '../components/ui/panel'
import { Chip } from '../components/ui/tone'
import {
  api,
  type Domain,
  type Health,
  type OutboundStatus,
  type SuppressionEntry,
} from '../lib/api'
import type { Route } from '../app/nav'
import { useSession } from '../lib/session'
import { cn } from '../lib/utils'

/**
 * Overview — stacked, per the design's own brief: **can we send, then what is it doing.**
 *
 * What is *not* here is the point. The design draws send rate, opens, replies, bounce and a
 * 24-hour chart. Nothing sends yet, so every one of those would be a number with nothing behind
 * it — and a dashboard of plausible fiction is worse than an empty one. What this screen shows is
 * what the API can actually answer today, and it says plainly that the rest is not running.
 */
export function OverviewScreen({ onNavigate }: { onNavigate: (route: Route) => void }) {
  const { can } = useSession()
  const queryClient = useQueryClient()

  const outbound = useQuery({
    queryKey: ['outbound'],
    queryFn: () => api.get<OutboundStatus>('/system/outbound'),
  })

  const health = useQuery({
    queryKey: ['health'],
    queryFn: () => api.get<Health>('/health'),
    refetchInterval: 15_000,
  })

  // The refusal now has nowhere to appear but the page — there is no dialog to hold it.
  const [error, setError] = useState<string | null>(null)

  const setOutbound = useMutation({
    mutationFn: ({ enabled, reason }: { enabled: boolean; reason: string }) =>
      api.post('/system/outbound', { enabled, reason: reason || undefined }),
    onSuccess: async () => {
      setError(null)
      await queryClient.invalidateQueries({ queryKey: ['outbound'] })
      await queryClient.invalidateQueries({ queryKey: ['health'] })
    },
    onError: (e: Error) => setError(e.message),
  })

  // Three facts, not one. `enabled` is the effective answer — both tiers agreeing — and the other
  // two say which tier is responsible when it is off.
  const known = outbound.isSuccess
  const effective = outbound.data?.enabled ?? false
  const orgOn = outbound.data?.organization ?? false
  const stoppedByPlatform = outbound.data?.platform === false
  const allowed = can('pause_system')

  return (
    <Screen>
      <ScreenHeader
        title="Overview"
        description="Whether this organization can send, and whether the system behind it is answering."
      />

      <div className="grid gap-4 xl:grid-cols-[minmax(0,1.35fr)_minmax(0,1fr)]">
        <div className="space-y-4">
          <SendingPanel
            known={known}
            loading={outbound.isLoading}
            failed={outbound.isError}
            effective={effective}
            orgOn={orgOn}
            stoppedByPlatform={stoppedByPlatform}
            allowed={allowed}
            error={error}
            onEnable={() =>
              setOutbound.mutate({ enabled: true, reason: 'Enabled from the console.' })
            }
            onStop={() => setOutbound.mutate({ enabled: false, reason: '' })}
          />

          <NotRunning onNavigate={onNavigate} />
        </div>

        <div className="space-y-4">
          <ServicePanel health={health.data} failed={health.isError} loading={health.isLoading} />
          <Counts />
        </div>
      </div>

      {/*
        One press, both directions — owner instruction, 2026-08-27.

        §57 still gets a reason; the console writes it rather than asking a person to type one. The
        friction was buying nothing real: a required sentence answered with "dfdgfdsgfd" is not a
        record of a decision, it is a toll in front of a button. What actually stops a bad send is
        unchanged and sits elsewhere — two kill-switch tiers that must both be open, §64 approval
        per recipient, and §30's preflight refusing anything malformed.
      */}
    </Screen>
  )
}

function SendingPanel({
  known,
  loading,
  failed,
  effective,
  orgOn,
  stoppedByPlatform,
  allowed,
  error,
  onEnable,
  onStop,
}: {
  known: boolean
  loading: boolean
  failed: boolean
  effective: boolean
  orgOn: boolean
  stoppedByPlatform: boolean
  allowed: boolean
  error: string | null
  onEnable: () => void
  onStop: () => void
}) {
  return (
    <Panel>
      <div className="p-5">
        {!known ? (
          <div className="space-y-3">
            <Skeleton className="h-7 w-56" />
            <Skeleton className="h-4 w-80" />
            {failed && (
              <p className="text-bad text-[12.5px]">
                The switch could not be read. Until it can be, treat sending as stopped (§49).
              </p>
            )}
            {loading && <span className="sr-only">Reading the current state</span>}
          </div>
        ) : (
          <>
            <div className="flex items-start gap-3">
              <span
                className={cn(
                  'mt-1 flex h-8 w-8 shrink-0 items-center justify-center rounded-full',
                  effective ? 'bg-okbg text-ok' : 'bg-badbg text-bad',
                )}
              >
                <Icon name={effective ? 'check' : 'suppressions'} size={17} />
              </span>
              <div className="min-w-0">
                <h2 className="text-text text-[17px] font-semibold tracking-[-0.02em]">
                  {effective ? 'This organization can send' : 'Nothing can send'}
                </h2>
                <p className="text-muted mt-1 max-w-[60ch] text-[12.5px] text-pretty">
                  {effective
                    ? 'Campaigns that are not in dry run can dispatch real email.'
                    : orgOn
                      ? 'Your own switch is on. Sending is stopped at the platform level by Axmion, for every organization, and will resume when that is lifted.'
                      : stoppedByPlatform
                        ? 'Both switches are off. This is the safe default and how the system ships.'
                        : 'Your organization’s switch is off.'}
                </p>
              </div>
            </div>

            <div className="border-line2 mt-5 grid gap-px border-t pt-5 sm:grid-cols-2">
              <Tier label="Your organization" on={orgOn} />
              <Tier label="Axmion, platform-wide" on={!stoppedByPlatform} />
            </div>
          </>
        )}
      </div>

      <div className="border-line bg-panel2 flex flex-wrap items-center justify-between gap-3 border-t px-5 py-3">
        <p className="text-faint text-[11.5px]">
          Sending needs both switches on. An unreadable switch counts as off.
        </p>
        {error && (
          <p role="alert" className="text-bad mb-2 text-[12.5px]">
            {error}
          </p>
        )}
        {allowed ? (
          // The button acts on the switch this organization OWNS, never on the effective answer.
          // Driving it from the effective value means that with the platform tier off, a
          // successful enable changes nothing on screen and the operator presses it again.
          orgOn ? (
            <Button variant="dangerQuiet" onClick={onStop}>
              Stop sending
            </Button>
          ) : (
            <Button variant="primary" onClick={onEnable}>
              Enable sending
            </Button>
          )
        ) : (
          // A control with nowhere to go is disabled and says why, never pointed at nothing.
          <Button disabled title="You do not have the pause_system permission">
            Requires pause_system
          </Button>
        )}
      </div>
    </Panel>
  )
}

function Tier({ label, on }: { label: string; on: boolean }) {
  return (
    <div className="flex items-center gap-2.5">
      <Chip tone={on ? 'ok' : 'bad'}>{on ? 'on' : 'off'}</Chip>
      <span className="text-muted text-[12.5px]">{label}</span>
    </div>
  )
}

/**
 * The platform's own liveness.
 *
 * "Last database probe", not "Database" — the value is what the probe returned, and P030 is the
 * case where the probe said `false` for thirty seconds at a time while the same process served
 * real queries in under half a second.
 */
function ServicePanel({
  health,
  failed,
  loading,
}: {
  health?: Health
  failed: boolean
  loading: boolean
}) {
  const probeFailed = health ? !health.checks.database : false

  return (
    <Panel
      title="Service"
      description="The platform's own health, shared by every organization."
    >
      <Row label="API">
        {failed ? (
          <Chip tone="bad">no answer</Chip>
        ) : loading ? (
          <Skeleton className="h-4 w-16" />
        ) : (
          <Chip tone={health?.status === 'ok' ? 'ok' : 'warn'}>{health?.status ?? '—'}</Chip>
        )}
      </Row>
      <Row label="Last database probe">
        {failed ? '—' : loading ? <Skeleton className="h-4 w-20" /> : probeFailed ? 'failed' : 'answered'}
      </Row>
      <Row label="Checked at">
        {health ? new Date(health.timestamp).toLocaleTimeString() : '—'}
      </Row>

      {probeFailed && (
        <p className="text-muted border-line2 border-t px-4 py-3 text-[11.5px]">
          A failed probe is not proof the database is down — see P030. It does mean nothing here
          should be trusted until it answers again.
        </p>
      )}
    </Panel>
  )
}

/** Counts the API can actually answer. Each is a real query, not a placeholder. */
function Counts() {
  const { can } = useSession()

  const contacts = useQuery({
    queryKey: ['contacts', '', 0],
    queryFn: () => api.get<{ total: number }>('/contacts?take=1'),
    enabled: can('view_contacts'),
  })

  const domains = useQuery({
    queryKey: ['domains'],
    queryFn: () => api.get<Domain[]>('/domains'),
    enabled: can('manage_domains'),
  })

  const suppressions = useQuery({
    queryKey: ['suppression'],
    queryFn: () => api.get<SuppressionEntry[]>('/suppression'),
    enabled: can('manage_suppression'),
  })

  const unhealthy =
    domains.data?.filter((d) => d.health !== 'HEALTHY' && d.state !== 'RETIRED').length ?? 0

  return (
    <Panel title="Audience and sending">
      {can('view_contacts') && (
        <Row label="Contacts">{contacts.data ? contacts.data.total.toLocaleString() : '—'}</Row>
      )}
      {can('manage_domains') && (
        <Row label="Domains">
          {domains.data ? (
            <span className="flex items-center gap-2">
              {domains.data.length.toLocaleString()}
              {unhealthy > 0 && <Chip tone="warn">{unhealthy} need attention</Chip>}
            </span>
          ) : (
            '—'
          )}
        </Row>
      )}
      {can('manage_suppression') && (
        <Row label="Suppressed addresses">
          {suppressions.data ? suppressions.data.length.toLocaleString() : '—'}
        </Row>
      )}
      {!can('view_contacts') && !can('manage_domains') && !can('manage_suppression') && (
        <Blank
          icon="denied"
          title="Nothing to show"
          body="Your role does not include contacts, domains or the suppression list."
        />
      )}
    </Panel>
  )
}

/**
 * What is deliberately not here.
 *
 * The design draws send rate, opens, replies and a 24-hour chart. Saying so is better than either
 * inventing the numbers or silently omitting the section — an operator looking for throughput
 * should learn in one sentence that there is none yet.
 */
function NotRunning({ onNavigate }: { onNavigate: (route: Route) => void }) {
  return (
    <Panel title="Not running yet">
      <div className="px-4 py-3">
        <p className="text-muted text-[12.5px] text-pretty">
          There is no dispatcher, so there is no send rate, no delivery, bounce or reply figures,
          and no campaign activity. Those arrive with the send pipeline; until then this screen
          shows only what the system can actually answer.
        </p>
        {/* Each is a real screen. Clicking through shows the columns it will have and names
            the endpoint that is missing, which is more useful than a chip that does nothing. */}
        <div className="mt-3 flex flex-wrap gap-1.5">
          {(
            [
              ['mailboxes', 'Mailboxes'],
              ['campaigns', 'Campaigns'],
              ['replies', 'Replies'],
              ['warmup', 'Warmup'],
              ['deliverability', 'Deliverability'],
            ] as Array<[Route, string]>
          ).map(([id, label]) => (
            <button key={id} onClick={() => onNavigate(id)} className="cursor-pointer">
              <Chip>{label}</Chip>
            </button>
          ))}
        </div>
      </div>
    </Panel>
  )
}
